import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import { s } from './TissStyles';
import { formatDateTime, formatDate } from './TissHelpers';
import { descreverGlosa } from './TissGlosas';
import { StatusBadge, useToast } from './TissShared';

function formatGlosa(code) {
  if (!code) return '';
  const desc = descreverGlosa(code);
  return desc ? `Glosa ${String(code).trim()}: ${desc}` : `Glosa: ${code}`;
}

const STATUS_COLORS_MAP = {
  autorizado: { bg: '#e8f5e9', color: '#2e7d32', cell: '#4caf50' },
  realizado: { bg: '#e3f2fd', color: '#1565c0', cell: '#1976d2' },
  enviado: { bg: '#fff3e0', color: '#e65100', cell: '#ff9800' },
  pendente: { bg: '#fff3e0', color: '#e65100', cell: '#ff9800' },
  negado: { bg: '#ffebee', color: '#c62828', cell: '#e53935' },
  erro: { bg: '#fce4ec', color: '#c62828', cell: '#c62828' },
};

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const MESES = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function getDominantStatus(dayData) {
  if (!dayData || dayData.total === 0) return null;
  let max = 0, dominant = null;
  for (const [k, v] of Object.entries(dayData)) {
    if (k !== 'total' && v > max) { max = v; dominant = k; }
  }
  return dominant;
}

function getDayColor(dayData) {
  const dominant = getDominantStatus(dayData);
  if (!dominant) return '#f5f5f5';
  return STATUS_COLORS_MAP[dominant]?.cell || '#e0e0e0';
}

function getDayOpacity(dayData, maxTotal) {
  if (!dayData || dayData.total === 0 || maxTotal === 0) return 1;
  return 0.35 + 0.65 * (dayData.total / maxTotal);
}

// ==================== CALENDAR COMPONENT ====================

function CalendarioAtividade({ dailyStats, calAno, calMes, onChangeMonth }) {
  const daysInMonth = new Date(calAno, calMes, 0).getDate();
  const firstDayOfWeek = new Date(calAno, calMes - 1, 1).getDay(); // 0=Dom
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === calAno && today.getMonth() + 1 === calMes;

  const maxTotal = useMemo(() => {
    let m = 0;
    Object.values(dailyStats).forEach(d => { if (d.total > m) m = d.total; });
    return m;
  }, [dailyStats]);

  const daysWithData = Object.keys(dailyStats).filter(k => dailyStats[k].total > 0);
  const totalMes = daysWithData.reduce((sum, k) => sum + dailyStats[k].total, 0);

  // Resumo do mes por status
  const resumoMes = useMemo(() => {
    const r = { autorizado: 0, realizado: 0, enviado: 0, pendente: 0, negado: 0, erro: 0 };
    Object.values(dailyStats).forEach(d => {
      Object.entries(d).forEach(([k, v]) => { if (k !== 'total' && r[k] !== undefined) r[k] += v; });
    });
    return r;
  }, [dailyStats]);

  const [tooltip, setTooltip] = useState(null);

  const cells = [];
  // Empty cells before first day
  for (let i = 0; i < firstDayOfWeek; i++) {
    cells.push(<div key={`empty-${i}`} style={{ ...s.calendarCell, backgroundColor: 'transparent' }} />);
  }
  // Day cells
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${calAno}-${String(calMes).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayData = dailyStats[dateKey];
    const hasData = dayData && dayData.total > 0;
    const color = getDayColor(dayData);
    const opacity = getDayOpacity(dayData, maxTotal);
    const isToday = isCurrentMonth && today.getDate() === day;

    cells.push(
      <div
        key={day}
        style={{
          ...s.calendarCell,
          backgroundColor: hasData ? color : '#f8f8f8',
          opacity: hasData ? opacity : 1,
          color: hasData ? '#fff' : '#bbb',
          fontWeight: isToday ? '700' : '500',
          boxShadow: isToday ? '0 0 0 2px #1976d2' : 'none',
          cursor: hasData ? 'pointer' : 'default',
        }}
        onMouseEnter={() => hasData && setTooltip({ day, data: dayData, dateKey })}
        onMouseLeave={() => setTooltip(null)}
      >
        <span style={{ fontSize: '13px', lineHeight: 1 }}>{day}</span>
        {hasData && <span style={{ fontSize: '9px', opacity: 0.9, lineHeight: 1, marginTop: '1px' }}>{dayData.total}</span>}
      </div>
    );
  }

  return (
    <div style={s.card}>
      {/* Header */}
      <div style={s.calendarHeader}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>Calendario de Atividade</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
            {totalMes} guia{totalMes !== 1 ? 's' : ''} em {daysWithData.length} dia{daysWithData.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button style={s.btnSmall} onClick={() => onChangeMonth(-1)}>&lsaquo;</button>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '140px', textAlign: 'center' }}>
            {MESES[calMes - 1]} {calAno}
          </span>
          <button style={s.btnSmall} onClick={() => onChangeMonth(1)}>&rsaquo;</button>
        </div>
      </div>

      {/* Mini resumo do mes */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {[
          { key: 'autorizado', label: 'Autorizadas' },
          { key: 'realizado', label: 'Realizadas' },
          { key: 'enviado', label: 'Enviadas' },
          { key: 'negado', label: 'Negadas' },
          { key: 'erro', label: 'Erros' },
        ].map(item => {
          const sc = STATUS_COLORS_MAP[item.key];
          const val = resumoMes[item.key] || 0;
          return (
            <div key={item.key} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '4px 10px', borderRadius: '12px',
              backgroundColor: val > 0 ? sc.bg : '#f5f5f5',
              fontSize: '12px', fontWeight: '600',
              color: val > 0 ? sc.color : '#aaa',
            }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: val > 0 ? sc.cell : '#ddd', flexShrink: 0 }} />
              {val} {item.label}
            </div>
          );
        })}
      </div>

      {/* Grid */}
      <div style={s.calendarGrid}>
        {WEEKDAYS.map(d => <div key={d} style={s.calendarWeekday}>{d}</div>)}
        {cells}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          marginTop: '10px', padding: '10px 14px', borderRadius: '8px',
          backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', fontSize: '12px',
        }}>
          <div style={{ fontWeight: '600', color: '#333', marginBottom: '6px' }}>
            {String(tooltip.day).padStart(2, '0')}/{String(calMes).padStart(2, '0')}/{calAno}
            <span style={{ fontWeight: '400', color: '#888', marginLeft: '8px' }}>{tooltip.data.total} guia{tooltip.data.total !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {Object.entries(tooltip.data)
              .filter(([k, v]) => k !== 'total' && v > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([status, count]) => {
                const sc = STATUS_COLORS_MAP[status] || { color: '#666', cell: '#999' };
                return (
                  <span key={status} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: sc.cell }} />
                    <span style={{ color: sc.color, fontWeight: '600' }}>{count}</span>
                    <span style={{ color: '#888', textTransform: 'capitalize' }}>{status}</span>
                  </span>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== DAILY TABLE COMPONENT ====================

function TabelaDiaria({ dailyStats, calAno, calMes }) {
  const daysWithData = useMemo(() => {
    return Object.entries(dailyStats)
      .filter(([, d]) => d.total > 0)
      .sort((a, b) => b[0].localeCompare(a[0])); // mais recente primeiro
  }, [dailyStats]);

  const statusCols = [
    { key: 'pendente', label: 'Pend.' },
    { key: 'enviado', label: 'Env.' },
    { key: 'autorizado', label: 'Aut.' },
    { key: 'realizado', label: 'Real.' },
    { key: 'negado', label: 'Neg.' },
    { key: 'erro', label: 'Erro' },
  ];

  // Totais do mes
  const totais = {};
  statusCols.forEach(c => { totais[c.key] = 0; });
  totais.total = 0;
  daysWithData.forEach(([, d]) => {
    statusCols.forEach(c => { totais[c.key] += d[c.key] || 0; });
    totais.total += d.total;
  });

  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Totais Diarios - {MESES[calMes - 1]} {calAno}</div>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Dia</th>
            {statusCols.map(c => <th key={c.key} style={{ ...s.th, textAlign: 'center', minWidth: '50px' }}>{c.label}</th>)}
            <th style={{ ...s.th, textAlign: 'center', fontWeight: '700' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {daysWithData.length === 0 ? (
            <tr>
              <td colSpan={statusCols.length + 2} style={{ ...s.td, textAlign: 'center', color: '#bbb', padding: '24px 12px', fontSize: '13px' }}>
                Nenhuma guia enviada neste mes
              </td>
            </tr>
          ) : daysWithData.map(([dateKey, dayData]) => {
            const day = parseInt(dateKey.split('-')[2]);
            const weekday = WEEKDAYS[new Date(parseInt(dateKey.split('-')[0]), parseInt(dateKey.split('-')[1]) - 1, day).getDay()];
            return (
              <tr key={dateKey} style={s.trHover}>
                <td style={{ ...s.td, fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap' }}>
                  {String(day).padStart(2, '0')} <span style={{ color: '#999', fontWeight: '400' }}>{weekday}</span>
                </td>
                {statusCols.map(c => {
                  const val = dayData[c.key] || 0;
                  const sc = STATUS_COLORS_MAP[c.key];
                  return (
                    <td key={c.key} style={{
                      ...s.td, textAlign: 'center', fontWeight: val > 0 ? '600' : '400',
                      color: val > 0 ? sc.color : '#ddd', fontSize: '12px',
                      backgroundColor: val > 0 ? sc.bg : 'transparent',
                    }}>
                      {val || '-'}
                    </td>
                  );
                })}
                <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', fontSize: '13px', color: '#333' }}>
                  {dayData.total}
                </td>
              </tr>
            );
          })}
        </tbody>
        {daysWithData.length > 0 && (
        <tfoot>
          <tr style={{ backgroundColor: '#f8f9fa' }}>
            <td style={{ ...s.td, fontWeight: '700', borderTop: '2px solid #e0e0e0' }}>Total</td>
            {statusCols.map(c => {
              const val = totais[c.key];
              const sc = STATUS_COLORS_MAP[c.key];
              return (
                <td key={c.key} style={{
                  ...s.td, textAlign: 'center', fontWeight: '700', borderTop: '2px solid #e0e0e0',
                  color: val > 0 ? sc.color : '#ddd', fontSize: '12px',
                }}>
                  {val || '-'}
                </td>
              );
            })}
            <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', fontSize: '14px', color: '#1976d2', borderTop: '2px solid #e0e0e0' }}>
              {totais.total}
            </td>
          </tr>
        </tfoot>
        )}
      </table>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

export default function TabPainel({ onNavigateGuias, dataInicio, dataFim }) {
  const [stats, setStats] = useState(null);
  const [worker, setWorker] = useState(null);
  const [recentes, setRecentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  // Elegibilidade widget
  const [carteirinha, setCarteirinha] = useState('');
  const [elegResultado, setElegResultado] = useState(null);
  const [verificando, setVerificando] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);

  // Calendario
  const now = new Date();
  const [calAno, setCalAno] = useState(now.getFullYear());
  const [calMes, setCalMes] = useState(now.getMonth() + 1);
  const [dailyStats, setDailyStats] = useState({});
  const [loadingCal, setLoadingCal] = useState(false);

  const load = useCallback(async () => {
    try {
      const periodo = {};
      if (dataInicio) periodo.dataInicio = dataInicio;
      if (dataFim) periodo.dataFim = dataFim;
      const [statsRes, workerRes, recentesRes] = await Promise.all([
        axios.get(`${API_URL}/tiss/guias/dashboard`, { params: periodo }),
        axios.get(`${API_URL}/tiss/worker/status`),
        axios.get(`${API_URL}/tiss/guias/recentes?limit=10`),
      ]);
      setStats(statsRes.data);
      setWorker(workerRes.data);
      setRecentes(recentesRes.data);
    } catch (err) {
      console.error('Erro ao carregar dashboard TISS:', err);
    } finally { setLoading(false); }
  }, [dataInicio, dataFim]);

  const loadDailyStats = useCallback(async () => {
    setLoadingCal(true);
    try {
      const res = await axios.get(`${API_URL}/tiss/guias/daily-stats?ano=${calAno}&mes=${calMes}`);
      // Pivot: rows -> { 'YYYY-MM-DD': { status: count, total: N } }
      const map = {};
      (res.data || []).forEach(r => {
        const key = typeof r.dia === 'string' ? r.dia.substring(0, 10) : new Date(r.dia).toISOString().substring(0, 10);
        if (!map[key]) map[key] = { total: 0 };
        map[key][r.status] = r.total;
        map[key].total = (map[key].total || 0) + r.total;
      });
      setDailyStats(map);
    } catch (err) {
      console.error('Erro ao carregar daily stats:', err);
    } finally { setLoadingCal(false); }
  }, [calAno, calMes]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDailyStats(); }, [loadDailyStats]);

  const handleChangeMonth = (delta) => {
    let m = calMes + delta;
    let y = calAno;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setCalMes(m);
    setCalAno(y);
  };

  const handleWorkerAction = async (action) => {
    try {
      await axios.post(`${API_URL}/tiss/worker/${action}`);
      const wRes = await axios.get(`${API_URL}/tiss/worker/status`);
      setWorker(wRes.data);
      toast.success(action === 'iniciar' ? 'Worker iniciado' : action === 'parar' ? 'Worker parado' : 'Ciclo executado');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao executar acao do worker');
    }
  };

  // Clickable stat cards
  const statCards = [
    { label: 'No período', value: stats?.total || 0, color: '#1976d2', filter: 'todas' },
    { label: 'Aguardando autorização', value: stats?.enviadas || 0, color: '#e65100', filter: 'enviada' },
    { label: 'Autorizadas', value: stats?.autorizadas || 0, color: '#2e7d32', filter: 'autorizada' },
    { label: 'Realizadas', value: stats?.realizadas || 0, color: '#0d47a1', filter: 'realizada' },
    { label: 'Negadas', value: stats?.negadas || 0, color: '#c62828', filter: 'problema' },
    { label: 'Erros', value: stats?.erros || 0, color: '#d32f2f', filter: 'problema' },
  ];

  // Elegibilidade
  const verificarElegibilidade = async () => {
    if (!carteirinha.trim()) return toast.warning('Informe a carteirinha');
    setVerificando(true);
    setElegResultado(null);
    try {
      const res = await axios.post(`${API_URL}/tiss/elegibilidade/verificar`, { carteirinha: carteirinha.trim() });
      setElegResultado(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao verificar');
    } finally { setVerificando(false); }
  };

  const loadHistorico = async () => {
    setLoadingHist(true);
    try {
      const res = await axios.get(`${API_URL}/tiss/elegibilidade/historico?limit=20`);
      setHistorico(res.data);
    } catch (err) { console.error('Erro historico:', err); }
    finally { setLoadingHist(false); }
  };

  const handleToggleHistorico = () => {
    if (!showHistorico) loadHistorico();
    setShowHistorico(!showHistorico);
  };

  if (loading) return <div style={s.emptyState}>Carregando...</div>;

  return (
    <>
      {/* Stats cards (clickable) — totais gerais (todas as datas) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', color: '#999', fontWeight: '500' }}>
          {dataInicio || dataFim ? 'Guias criadas no período selecionado' : 'Total geral (todas as datas)'}
        </span>
      </div>
      <div style={s.statsRow}>
        {statCards.map((c, i) => (
          <div key={i}
            style={{ ...s.statCard, borderLeftColor: c.color }}
            onClick={() => c.filter && onNavigateGuias?.(c.filter)}
            title={c.filter ? `Ver guias: ${c.label.toLowerCase()}` : undefined}
          >
            <div style={s.statLabel}>{c.label}</div>
            <div style={s.statValue}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Worker de envio automático — travado no servidor (TISS_WORKER_LIBERADO). Sem botão de
          ligar na tela: envio automático errado vira guia de verdade na UNIMED. */}
      <div style={{ ...s.workerBox, ...(worker?.rodando ? s.workerRunning : s.workerStopped) }}>
        <div style={{ ...s.workerDot, backgroundColor: worker?.rodando ? '#4caf50' : '#9e9e9e' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
            Envio automático: {worker?.rodando ? 'ATIVO' : worker?.travado ? 'travado' : 'parado'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {worker?.travado
              ? 'As guias são enviadas uma a uma pela aba Guias. O envio automático só pode ser liberado no servidor.'
              : worker?.ultimo_ciclo ? `Último ciclo: ${formatDateTime(worker.ultimo_ciclo)}` : 'Desligado.'}
          </div>
        </div>
        {worker?.rodando && (
          <button style={s.btnDanger} onClick={() => handleWorkerAction('parar')}>Parar</button>
        )}
      </div>

      {/* Calendario + Tabela Diaria (side by side on large screens) */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '0', flexWrap: 'wrap' }}>
        <div style={{ flex: '1', minWidth: '320px' }}>
          {loadingCal ? (
            <div style={{ ...s.card, textAlign: 'center', padding: '40px', color: '#999' }}>Carregando calendario...</div>
          ) : (
            <CalendarioAtividade
              dailyStats={dailyStats}
              calAno={calAno}
              calMes={calMes}
              onChangeMonth={handleChangeMonth}
            />
          )}
        </div>
        <div style={{ flex: '1', minWidth: '380px' }}>
          <TabelaDiaria dailyStats={dailyStats} calAno={calAno} calMes={calMes} />
        </div>
      </div>

      {/* Elegibilidade Widget */}
      <div style={s.card}>
        <div style={s.cardTitle}>Verificar Elegibilidade</div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, maxWidth: '300px' }}>
            <label style={s.filterLabel}>Numero da Carteirinha</label>
            <input style={s.input} value={carteirinha} placeholder="0000000000000"
              onChange={e => setCarteirinha(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') verificarElegibilidade(); }} />
          </div>
          <button style={s.btnPrimary} onClick={verificarElegibilidade} disabled={verificando}>
            {verificando ? 'Verificando...' : 'Verificar'}
          </button>
          <button style={s.btnSecondary} onClick={handleToggleHistorico}>
            {showHistorico ? 'Ocultar Historico' : 'Ver Historico'}
          </button>
        </div>

        {elegResultado && (
          <div style={{
            marginTop: '16px', padding: '16px', borderRadius: '8px',
            backgroundColor: elegResultado.elegivel ? '#e8f5e9' : '#ffebee',
            border: `1px solid ${elegResultado.elegivel ? '#c8e6c9' : '#ffcdd2'}`,
          }}>
            <div style={{ fontWeight: '600', fontSize: '15px', color: elegResultado.elegivel ? '#2e7d32' : '#c62828', marginBottom: '8px' }}>
              {elegResultado.elegivel ? 'Beneficiario Elegivel' : 'Beneficiario Nao Elegivel'}
            </div>
            {elegResultado.nome_beneficiario && <div style={{ fontSize: '13px', marginBottom: '4px' }}><strong>Nome:</strong> {elegResultado.nome_beneficiario}</div>}
            {elegResultado.nome_plano && <div style={{ fontSize: '13px', marginBottom: '4px' }}><strong>Plano:</strong> {elegResultado.nome_plano}</div>}
            {elegResultado.data_validade && <div style={{ fontSize: '13px', marginBottom: '4px' }}><strong>Validade:</strong> {formatDate(elegResultado.data_validade)}</div>}
            {elegResultado.motivo && <div style={{ fontSize: '13px', marginBottom: '4px' }}><strong>Motivo:</strong> {elegResultado.motivo}</div>}
            <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>Fonte: {elegResultado.fonte || 'operadora'}</div>
          </div>
        )}

        {/* Inline historico table */}
        {showHistorico && (
          <div style={{ marginTop: '16px' }}>
            {loadingHist ? <div style={{ color: '#999' }}>Carregando...</div> : historico.length === 0 ? (
              <div style={{ color: '#999', fontSize: '13px' }}>Nenhuma verificacao realizada</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Carteirinha</th>
                    <th style={s.th}>Beneficiario</th>
                    <th style={s.th}>Plano</th>
                    <th style={s.th}>Elegivel</th>
                    <th style={s.th}>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map(h => (
                    <tr key={h.id}>
                      <td style={s.td}>{h.numero_carteira}</td>
                      <td style={s.td}>{h.nome_beneficiario || '-'}</td>
                      <td style={s.td}>{h.nome_plano || '-'}</td>
                      <td style={s.td}>
                        <span style={{
                          ...s.badge,
                          backgroundColor: h.elegivel ? '#e8f5e9' : '#ffebee',
                          color: h.elegivel ? '#2e7d32' : '#c62828',
                        }}>{h.elegivel ? 'Sim' : 'Nao'}</span>
                      </td>
                      <td style={s.td}>{formatDateTime(h.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div style={s.card}>
        <div style={s.cardTitle}>Atividade Recente</div>
        {recentes.length === 0 ? (
          <div style={s.emptyState}>
            <div style={s.emptyIcon}>-</div>
            <p style={s.emptyText}>Nenhuma guia enviada ainda</p>
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Guia</th>
                <th style={s.th}>Paciente</th>
                <th style={s.th}>Protocolo</th>
                <th style={s.th}>Data</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {recentes.map(g => (
                <tr key={g.id} style={{ ...s.trHover, cursor: 'pointer' }}
                  onClick={() => navigate(`/tiss-unimed/protocolo/${g.solicitacao_local}/${g.solicitacao_protocolo}`)}>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>{g.numero_guia_prestador}</td>
                  <td style={s.td}>{g.paciente_nome}</td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>{g.solicitacao_local}/{g.solicitacao_protocolo}</td>
                  <td style={{ ...s.td, fontSize: '12px' }}>{formatDateTime(g.created_at)}</td>
                  <td style={s.td}><StatusBadge status={g.status} /></td>
                  <td style={s.td}>
                    {g.ultimo_erro ? (
                      <span style={{ fontSize: '11px', color: '#c62828', display: 'flex', alignItems: 'center', gap: '4px' }}
                        title={g.ultimo_erro}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#c62828', flexShrink: 0 }} />
                        {g.ultimo_erro.startsWith('HTTP') ? g.ultimo_erro : g.ultimo_erro.substring(0, 60)}
                      </span>
                    ) : g.motivo_negativa ? (
                      <span style={{ fontSize: '11px', color: '#e65100', display: 'flex', alignItems: 'center', gap: '4px' }}
                        title={formatGlosa(g.motivo_negativa)}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#e65100', flexShrink: 0 }} />
                        {formatGlosa(g.motivo_negativa)}
                      </span>
                    ) : g.numero_autorizacao ? (
                      <span style={{ fontSize: '11px', color: '#2e7d32', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#2e7d32', flexShrink: 0 }} />
                        Senha: {g.numero_autorizacao}
                      </span>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#888' }}>{g.tipo_envio || '-'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
